import { useState, useEffect } from "react";
import Navigation from "../components/Navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { 
  Users, 
  Shield, 
  Search, 
  Filter, 
  UserCheck, 
  UserX, 
  Edit, 
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  Mail,
  Megaphone,
  TrendingUp,
  UserPlus,
  Activity,
  BarChart3,
  PieChart,
  Calendar,
  RefreshCw,
  Settings
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { LineChart, Line, BarChart, Bar, PieChart as RePieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d'];

export default function UserManagementEnhanced({ user, onLogout }) {
  const { user: authUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [canManage, setCanManage] = useState(false);
  const [requesterRole, setRequesterRole] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(true);
  
  // Modals
  const [showGrantPermission, setShowGrantPermission] = useState(false);
  const [showRoleUpdate, setShowRoleUpdate] = useState(false);
  const [showPermissionHistory, setShowPermissionHistory] = useState(false);
  const [showUserActivity, setShowUserActivity] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [showAnnouncementCreator, setShowAnnouncementCreator] = useState(false);
  const [showTeamManager, setShowTeamManager] = useState(false);
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [userActivity, setUserActivity] = useState(null);
  const [permissionHistory, setPermissionHistory] = useState([]);
  const [teams, setTeams] = useState([]);
  
  // Form states
  const [selectedPermission, setSelectedPermission] = useState("view_team_reports");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [announcementTitle, setAnnouncementTitle] = useState("");
  const [announcementContent, setAnnouncementContent] = useState("");
  const [announcementTargetRoles, setAnnouncementTargetRoles] = useState([]);

  useEffect(() => {
    fetchUsers();
    fetchAnalytics();
    fetchTeams();
  }, []);

  useEffect(() => {
    filterAndSortUsers();
  }, [users, searchTerm, roleFilter, statusFilter, sortField, sortDirection]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/manage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUsers(response.data.users);
      setCanManage(response.data.can_manage);
      setRequesterRole(response.data.requester_role);
    } catch (error) {
      console.error("Failed to load users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error("Failed to load analytics:", error);
    }
  };

  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/teams/manage/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data.teams);
    } catch (error) {
      console.error("Failed to load teams:", error);
    }
  };

  const filterAndSortUsers = () => {
    let filtered = users;
    
    if (searchTerm) {
      filtered = filtered.filter(u => 
        u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (roleFilter) {
      filtered = filtered.filter(u => u.role === roleFilter);
    }

    if (statusFilter === "active") {
      filtered = filtered.filter(u => u.is_active !== false);
    } else if (statusFilter === "inactive") {
      filtered = filtered.filter(u => u.is_active === false);
    }
    
    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortField] || "";
      let bVal = b[sortField] || "";
      
      if (sortField === "last_login_at") {
        aVal = a.last_login_at ? new Date(a.last_login_at) : new Date(0);
        bVal = b.last_login_at ? new Date(b.last_login_at) : new Date(0);
      }
      
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    
    setFilteredUsers(filtered);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleGrantPermission = async () => {
    if (!selectedUser) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/permissions/grant`, {
        user_id: selectedUser.id,
        permission_type: selectedPermission
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Permission granted successfully!");
      setShowGrantPermission(false);
      fetchUsers();
    } catch (error) {
      console.error("Failed to grant permission:", error);
      toast.error(error.response?.data?.detail || "Failed to grant permission");
    }
  };

  const handleRevokePermission = async (permissionId) => {
    if (!window.confirm("Are you sure you want to revoke this permission?")) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/permissions/${permissionId}/revoke`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Permission revoked successfully!");
      fetchUsers();
    } catch (error) {
      console.error("Failed to revoke permission:", error);
      toast.error("Failed to revoke permission");
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedUser || !selectedRole) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/users/${selectedUser.id}/role`, null, {
        params: { new_role: selectedRole },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("User role updated successfully!");
      setShowRoleUpdate(false);
      fetchUsers();
    } catch (error) {
      console.error("Failed to update role:", error);
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    const action = currentStatus ? "deactivate" : "activate";
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/users/${userId}/status`, null, {
        params: { is_active: !currentStatus },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`User ${action}d successfully!`);
      fetchUsers();
    } catch (error) {
      console.error("Failed to update user status:", error);
      toast.error(error.response?.data?.detail || "Failed to update user status");
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm("Send password reset email to this user?")) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/users/${userId}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Password reset email sent!");
    } catch (error) {
      console.error("Failed to send reset email:", error);
      toast.error("Failed to send reset email");
    }
  };

  const handleExportUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'users_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Users exported successfully!");
    } catch (error) {
      console.error("Failed to export users:", error);
      toast.error("Failed to export users");
    }
  };

  const fetchUserActivity = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/${userId}/activity`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUserActivity(response.data);
      setShowUserActivity(true);
    } catch (error) {
      console.error("Failed to load user activity:", error);
      toast.error("Failed to load user activity");
    }
  };

  const fetchPermissionHistory = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/permissions/history`, {
        params: { target_user_id: userId },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPermissionHistory(response.data.permissions);
      setShowPermissionHistory(true);
    } catch (error) {
      console.error("Failed to load permission history:", error);
      toast.error("Failed to load permission history");
    }
  };

  const handleSendEmail = async () => {
    if (selectedUsers.length === 0) {
      toast.error("Please select at least one user");
      return;
    }
    
    if (!emailSubject || !emailMessage) {
      toast.error("Please fill in subject and message");
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/communications/send-email`, {
        recipient_user_ids: selectedUsers,
        subject: emailSubject,
        message: emailMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Email sent successfully!");
      setShowEmailComposer(false);
      setEmailSubject("");
      setEmailMessage("");
      setSelectedUsers([]);
    } catch (error) {
      console.error("Failed to send email:", error);
      toast.error("Failed to send email");
    }
  };

  const handleCreateAnnouncement = async () => {
    if (!announcementTitle || !announcementContent) {
      toast.error("Please fill in title and content");
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/announcements/create`, {
        title: announcementTitle,
        content: announcementContent,
        target_roles: announcementTargetRoles
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Announcement created successfully!");
      setShowAnnouncementCreator(false);
      setAnnouncementTitle("");
      setAnnouncementContent("");
      setAnnouncementTargetRoles([]);
    } catch (error) {
      console.error("Failed to create announcement:", error);
      toast.error("Failed to create announcement");
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'system-admin': 'bg-red-100 text-red-800 border-red-300',
      'school-admin': 'bg-purple-100 text-purple-800 border-purple-300',
      'team-admin': 'bg-blue-100 text-blue-800 border-blue-300',
      'coach': 'bg-green-100 text-green-800 border-green-300',
      'sport-performance-consultant': 'bg-teal-100 text-teal-800 border-teal-300',
      'athlete': 'bg-gray-100 text-gray-800 border-gray-300',
      'parent': 'bg-orange-100 text-orange-800 border-orange-300'
    };
    return colors[role] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const formatLastLogin = (lastLogin) => {
    if (!lastLogin) return 'Never';
    const date = new Date(lastLogin);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;
    return date.toLocaleDateString();
  };

  if (!authUser || !['system-admin', 'school-admin', 'team-admin', 'coach', 'sport-performance-consultant'].includes(authUser.role)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50">
        <Navigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 py-12">
          <Card>
            <CardHeader>
              <CardTitle className="text-red-600">Access Denied</CardTitle>
              <CardDescription>You don&apos;t have permission to access user management.</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <Navigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">
              User Management Dashboard
            </h1>
            <p className="text-gray-600">
              <Shield className="inline w-5 h-5 mr-2" />
              Manage users, permissions, and analytics ({requesterRole})
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowAnalytics(!showAnalytics)} variant="outline">
              <BarChart3 className="w-4 h-4 mr-2" />
              {showAnalytics ? 'Hide' : 'Show'} Analytics
            </Button>
            {canManage && (
              <>
                <Button onClick={handleExportUsers} variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <Button onClick={() => setShowTeamManager(true)} variant="outline">
                  <Users className="w-4 h-4 mr-2" />
                  Teams
                </Button>
                <Button onClick={() => setShowEmailComposer(true)} className="bg-blue-600 hover:bg-blue-700">
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </Button>
                <Button onClick={() => setShowAnnouncementCreator(true)} className="bg-purple-600 hover:bg-purple-700">
                  <Megaphone className="w-4 h-4 mr-2" />
                  Announce
                </Button>
              </>
            )}
            <Button onClick={fetchUsers} variant="outline">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Analytics Dashboard */}
        {showAnalytics && analytics && (
          <div className="mb-8 space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Users</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-indigo-600">{analytics.total_users}</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600">Active (30 days)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-600">{analytics.active_users_30d}</div>
                  <p className="text-xs text-gray-500 mt-1">
                    {Math.round((analytics.active_users_30d / analytics.total_users) * 100)}% of total
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600">New This Month</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-blue-600">{analytics.new_users_this_month}</div>
                  <p className="text-xs text-gray-500 mt-1">+ {analytics.new_users_this_month} users</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600">Inactive Users</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-red-600">{analytics.inactive_users}</div>
                  <p className="text-xs text-gray-500 mt-1">Deactivated accounts</p>
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* User Growth Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">User Growth (6 Months)</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={analytics.user_growth}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="count" stroke="#8884d8" name="New Users" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Role Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Users by Role</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={Object.entries(analytics.users_by_role).map(([role, count]) => ({ role: role.replace('-', ' '), count }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="role" angle={-15} textAnchor="end" height={80} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Filters and Actions */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-xl">Search, Filter & Sort</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <Label>Search Users</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div>
                <Label>Filter by Role</Label>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="">All Roles</option>
                  <option value="athlete">Athlete</option>
                  <option value="coach">Coach</option>
                  <option value="team-admin">Team Administrator</option>
                  <option value="sport-performance-consultant">Sport Consultant</option>
                  <option value="school-admin">School Administrator</option>
                  <option value="system-admin">System Administrator</option>
                </select>
              </div>

              <div>
                <Label>Filter by Status</Label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="all">All Users</option>
                  <option value="active">Active Only</option>
                  <option value="inactive">Inactive Only</option>
                </select>
              </div>
              
              <div className="flex items-end gap-2">
                {canManage && selectedUsers.length > 0 && (
                  <Button
                    onClick={() => setShowEmailComposer(true)}
                    className="bg-purple-600 hover:bg-purple-700 w-full"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email {selectedUsers.length} Selected
                  </Button>
                )}
              </div>
            </div>
            
            <div className="text-sm text-gray-600">
              Showing {filteredUsers.length} of {users.length} users
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle>Users</CardTitle>
            <CardDescription>
              {canManage ? "Manage user roles, permissions, and status" : "View user information (read-only)"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading users...</div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No users found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      {canManage && requesterRole === 'system-admin' && (
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          <input
                            type="checkbox"
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUsers(filteredUsers.map(u => u.id));
                              } else {
                                setSelectedUsers([]);
                              }
                            }}
                            checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                          />
                        </th>
                      )}
                      <th 
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('name')}
                      >
                        User {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('role')}
                      >
                        Role {sortField === 'role' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">School/Club</th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('last_login_at')}
                      >
                        Last Active {sortField === 'last_login_at' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Permissions</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredUsers.map((userItem) => (
                      <tr key={userItem.id} className="hover:bg-gray-50">
                        {canManage && requesterRole === 'system-admin' && (
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(userItem.id)}
                              onChange={() => toggleUserSelection(userItem.id)}
                            />
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900">{userItem.name}</span>
                            <span className="text-sm text-gray-500">{userItem.email}</span>
                            <span className="text-xs text-gray-400">Logins: {userItem.login_count || 0}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${getRoleBadgeColor(userItem.role)}`}>
                            {userItem.role}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {userItem.school_club_name || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatLastLogin(userItem.last_login_at)}
                        </td>
                        <td className="px-4 py-3">
                          {userItem.is_active === false ? (
                            <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-full">
                              Inactive
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                              Active
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {userItem.permissions && userItem.permissions.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {userItem.permissions.slice(0, 2).map((perm) => (
                                <span key={perm.id} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                                  {perm.permission_type}
                                </span>
                              ))}
                              {userItem.permissions.length > 2 && (
                                <span className="text-xs text-gray-500">+{userItem.permissions.length - 2}</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">None</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => fetchUserActivity(userItem.id)}
                              title="View Activity"
                            >
                              <Activity className="w-3 h-3" />
                            </Button>
                            {canManage && userItem.can_edit && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedUser(userItem);
                                    setSelectedRole(userItem.role);
                                    setShowRoleUpdate(true);
                                  }}
                                  title="Edit Role"
                                >
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleToggleUserStatus(userItem.id, userItem.is_active !== false)}
                                  className={userItem.is_active === false ? 'text-green-600' : 'text-red-600'}
                                  title={userItem.is_active === false ? 'Activate User' : 'Deactivate User'}
                                >
                                  {userItem.is_active === false ? <CheckCircle className="w-3 h-3" /> : <UserX className="w-3 h-3" />}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleResetPassword(userItem.id)}
                                  title="Reset Password"
                                >
                                  <RefreshCw className="w-3 h-3" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* User Activity Modal */}
      <Dialog open={showUserActivity} onOpenChange={setShowUserActivity}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>User Activity Details</DialogTitle>
          </DialogHeader>
          {userActivity && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-lg mb-2">{userActivity.user_name}</h3>
                <p className="text-sm text-gray-600">{userActivity.user_email}</p>
                <p className="text-sm text-gray-600">Role: {userActivity.user_role}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Total Logins</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">{userActivity.login_count}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Last Login</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">{userActivity.last_login_at ? new Date(userActivity.last_login_at).toLocaleString() : 'Never'}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Training Plans</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">{userActivity.stats.training_plans_created}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Goals Set</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">{userActivity.stats.goals_set}</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Composer Modal */}
      <Dialog open={showEmailComposer} onOpenChange={setShowEmailComposer}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Send Email to Users</DialogTitle>
            <DialogDescription>
              Sending to {selectedUsers.length} selected user(s)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Subject</Label>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Email subject..."
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Email message..."
                rows={6}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowEmailComposer(false)}>
                Cancel
              </Button>
              <Button onClick={handleSendEmail} className="bg-blue-600 hover:bg-blue-700">
                <Mail className="w-4 h-4 mr-2" />
                Send Email
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Announcement Creator Modal */}
      <Dialog open={showAnnouncementCreator} onOpenChange={setShowAnnouncementCreator}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Announcement</DialogTitle>
            <DialogDescription>
              Create a system-wide announcement
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={announcementTitle}
                onChange={(e) => setAnnouncementTitle(e.target.value)}
                placeholder="Announcement title..."
              />
            </div>
            <div>
              <Label>Content</Label>
              <Textarea
                value={announcementContent}
                onChange={(e) => setAnnouncementContent(e.target.value)}
                placeholder="Announcement content..."
                rows={6}
              />
            </div>
            <div>
              <Label>Target Roles (leave empty for all)</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {['athlete', 'coach', 'team-admin', 'school-admin'].map(role => (
                  <label key={role} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={announcementTargetRoles.includes(role)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAnnouncementTargetRoles([...announcementTargetRoles, role]);
                        } else {
                          setAnnouncementTargetRoles(announcementTargetRoles.filter(r => r !== role));
                        }
                      }}
                    />
                    <span className="text-sm">{role}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowAnnouncementCreator(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateAnnouncement} className="bg-purple-600 hover:bg-purple-700">
                <Megaphone className="w-4 h-4 mr-2" />
                Create Announcement
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Team Manager Modal */}
      <Dialog open={showTeamManager} onOpenChange={setShowTeamManager}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Team Management</DialogTitle>
            <DialogDescription>
              View and manage teams
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {teams.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No teams found</p>
            ) : (
              <div className="space-y-3">
                {teams.map((team) => (
                  <Card key={team.id}>
                    <CardHeader>
                      <CardTitle className="text-lg">{team.name}</CardTitle>
                      <CardDescription>{team.description || 'No description'}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600">School/Club:</p>
                          <p className="font-medium">{team.school_club_name || '-'}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Sport:</p>
                          <p className="font-medium">{team.sport || '-'}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Athletes:</p>
                          <p className="font-medium">{team.athlete_count || 0}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Coaches:</p>
                          <p className="font-medium">{team.coach_count || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Role Update & Permission History Modals (keeping original functionality) */}
      <Dialog open={showRoleUpdate} onOpenChange={setShowRoleUpdate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update User Role</DialogTitle>
            <DialogDescription>
              Change the role for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New Role</Label>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full p-2 border rounded-md mt-1"
              >
                <option value="athlete">Athlete</option>
                <option value="coach">Coach</option>
                <option value="team-admin">Team Administrator</option>
                <option value="sport-performance-consultant">Sport Consultant</option>
                {requesterRole === 'system-admin' && (
                  <>
                    <option value="school-admin">School Administrator</option>
                    <option value="system-admin">System Administrator</option>
                  </>
                )}
              </select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowRoleUpdate(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateRole} className="bg-blue-600 hover:bg-blue-700">
                Update Role
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showPermissionHistory} onOpenChange={setShowPermissionHistory}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Permission History</DialogTitle>
            <DialogDescription>
              View all permissions granted and revoked
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {permissionHistory.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No permission history found</p>
            ) : (
              permissionHistory.map((perm) => (
                <div key={perm.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${perm.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {perm.permission_type}
                      </span>
                      <span className={`ml-2 px-2 py-1 rounded text-xs ${perm.is_active ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                        {perm.is_active ? 'Active' : 'Revoked'}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(perm.granted_at).toLocaleDateString()}
                    </span>
                  </div>
                  {perm.granted_by_user && (
                    <div className="text-sm text-gray-600">
                      Granted by: <strong>{perm.granted_by_user.name}</strong> ({perm.granted_by_user.role})
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}